/**
 * NIGHTBOUND - activity resolution.
 *
 * An "action" is a short-lived thing the creature is doing (eating, training,
 * playing, being cleaned). The server records the action and when it happened;
 * both client and server derive the creature's current activity from that, so
 * no extra state needs to be written.
 */

import { SECOND_MS } from './constants'
import type { CreatureCondition, CreatureStateId } from './creatureState'
import { resolveCreatureState } from './creatureState'
import type { ActivityState, MoodId } from './species'

/** How long the visible reaction to each action lasts. */
export const ACTIVITY_DISPLAY_SECONDS: Record<ActivityState, number> = {
  IDLE: 0,
  SLEEPING: 0,
  EATING: 16,
  PLAYING: 14,
  CLEANING: 14,
  TRAINING: 18,
  HURT: 24,
}

export const ACTION_TO_ACTIVITY: Record<string, ActivityState> = {
  FEED: 'EATING',
  CLEAN: 'CLEANING',
  PLAY: 'PLAYING',
  TRAIN: 'TRAINING',
  PET: 'IDLE',
  SLEEP: 'SLEEPING',
  WAKE: 'IDLE',
  CREATE: 'IDLE',
}

export interface DerivedActivity {
  activity: ActivityState
  /** Seconds left of the visible reaction. */
  remainingSeconds: number
}

export function deriveActivity(input: {
  lastAction: string | null
  lastActionAt: Date | null
  sleeping: boolean
  now: Date
}): DerivedActivity {
  if (input.sleeping) return { activity: 'SLEEPING', remainingSeconds: 0 }

  const activity = input.lastAction ? (ACTION_TO_ACTIVITY[input.lastAction] ?? 'IDLE') : 'IDLE'
  const duration = ACTIVITY_DISPLAY_SECONDS[activity] ?? 0
  if (!input.lastActionAt || duration === 0) return { activity: 'IDLE', remainingSeconds: 0 }

  const elapsed = (input.now.getTime() - input.lastActionAt.getTime()) / SECOND_MS
  const remaining = duration - elapsed
  if (remaining <= 0) return { activity: 'IDLE', remainingSeconds: 0 }
  return { activity, remainingSeconds: Math.round(remaining) }
}

/** Condition flags derived from needs - used by art, overlays and copy. */
export function deriveCondition(input: {
  hunger: number
  hygiene: number
  energy: number
  currentHp: number
  maxHp: number
  sanity: number
  resting: boolean
}): CreatureCondition {
  return {
    hungry: input.hunger < 25,
    dirty: input.hygiene < 35,
    exhausted: input.energy < 20,
    injured: input.currentHp < input.maxHp * 0.7,
    disturbed: input.sanity < 35,
  }
}

export function visualStateFor(input: {
  activity: ActivityState
  mood: MoodId
  condition: CreatureCondition
}): CreatureStateId {
  return resolveCreatureState(input)
}

/** Human readable line describing what the creature is doing. */
export const ACTIVITY_LABELS: Record<ActivityState, string> = {
  IDLE: 'Watching',
  SLEEPING: 'Sleeping',
  EATING: 'Eating',
  PLAYING: 'Playing',
  CLEANING: 'Being cleaned',
  TRAINING: 'Training',
  HURT: 'Hurt',
}

/** Small glyphs shown on mobile where labels are tight. */
export const ACTIVITY_GLYPHS: Record<ActivityState, string> = {
  IDLE: '◦',
  SLEEPING: '☾',
  EATING: '✦',
  PLAYING: '◇',
  CLEANING: '≈',
  TRAINING: '⚒',
  HURT: '✕',
}
